import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge, PriorityBadge } from "@/components/common/StatusBadge";
import { TaskModal } from "@/components/Tasks/TaskModal";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { DEPARTMENTS, TASK_STATUSES, TASK_PRIORITIES } from "@/constants";
import { useAuth } from "@/context/AuthContext";
import { Plus } from "@phosphor-icons/react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function Tasks() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [filters, setFilters] = useState({ department: "", status: searchParams.get("status") || "", priority: "", search: "" });
  const [selected, setSelected] = useState([]);
  const [taskModal, setTaskModal] = useState(null);
  const canEdit = user?.role === "admin" || user?.role === "pm";

  const load = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/tasks", { params });
    setTasks(data);
  };

  useEffect(() => { load(); }, [filters]);

  useEffect(() => {
    const taskId = searchParams.get("task");
    if (taskId) {
      api.get(`/tasks/${taskId}`).then((r) => setTaskModal(r.data)).catch(() => {});
    }
  }, [searchParams]);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((i) => i !== id) : [...s, id]));

  const bulkUpdate = async (updates) => {
    if (selected.length === 0) return;
    await api.patch("/tasks/bulk", { task_ids: selected, updates });
    toast.success(`Updated ${selected.length} tasks`);
    setSelected([]);
    load();
  };

  return (
    <div data-testid="tasks-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <span className="label-caps">Cross-Project</span>
          <h1 className="text-2xl font-display font-extrabold mt-1">Tasks</h1>
        </div>
        {canEdit && <button data-testid="new-task-btn" onClick={() => setTaskModal({})} className="btn-navy flex items-center gap-2"><Plus size={14} />New Task</button>}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <Input data-testid="task-search-input" placeholder="Search by task, project, assignee..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="max-w-xs" style={{ borderRadius: "2px" }} />
        <Select value={filters.department} onValueChange={(v) => setFilters({ ...filters, department: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="task-department-filter" className="w-40" style={{ borderRadius: "2px" }}><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Depts</SelectItem>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="task-status-filter" className="w-40" style={{ borderRadius: "2px" }}><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="task-priority-filter" className="w-40" style={{ borderRadius: "2px" }}><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Priorities</SelectItem>{TASK_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {canEdit && selected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 p-3 bg-muted border border-border" style={{ borderRadius: "2px" }} data-testid="bulk-action-bar">
          <span className="text-xs font-semibold">{selected.length} selected</span>
          <Select onValueChange={(v) => bulkUpdate({ status: v })}>
            <SelectTrigger data-testid="bulk-status-select" className="w-40 h-8" style={{ borderRadius: "2px" }}><SelectValue placeholder="Set status..." /></SelectTrigger>
            <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <button data-testid="bulk-clear-btn" onClick={() => setSelected([])} className="text-xs text-muted-foreground hover:underline">Clear</button>
        </div>
      )}

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm" data-testid="tasks-table">
          <thead>
            <tr className="text-left border-b border-border">
              {canEdit && <th className="p-3 w-8"></th>}
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Task</th>
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Project</th>
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Assignee</th>
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Priority</th>
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Progress</th>
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Status</th>
              <th className="p-3 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Due</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id} className="tbl-row border-b border-border/50" data-testid={`task-table-row-${t.id}`}>
                {canEdit && <td className="p-3"><input type="checkbox" data-testid={`task-select-checkbox-${t.id}`} checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} /></td>}
                <td className="p-3 font-medium cursor-pointer" onClick={() => setTaskModal(t)}>{t.title}</td>
                <td className="p-3 text-muted-foreground">{t.project_name}</td>
                <td className="p-3 text-muted-foreground">{t.assignee_name || "Unassigned"}</td>
                <td className="p-3"><PriorityBadge priority={t.priority} /></td>
                <td className="p-3" data-testid={`task-progress-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <div className="w-14 h-1.5 bg-muted overflow-hidden" style={{ borderRadius: "2px" }}>
                      <div className="h-full bg-primary" style={{ width: `${t.completion_pct || 0}%` }} />
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">{t.completion_pct || 0}%</span>
                  </div>
                </td>
                <td className="p-3"><StatusBadge status={t.status} /></td>
                <td className="p-3 font-mono text-xs">{t.due_date ? format(new Date(t.due_date), "d MMM") : "—"}</td>
              </tr>
            ))}
            {tasks.length === 0 && <tr><td colSpan={8} className="p-4 text-sm font-mono text-muted-foreground">&gt; no tasks match these filters</td></tr>}
          </tbody>
        </table>
      </div>

      {taskModal && <TaskModal task={taskModal.id ? taskModal : null} onClose={() => setTaskModal(null)} onSaved={() => { setTaskModal(null); load(); }} />}
    </div>
  );
}
